import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../lib/api';
import { 
  Search, FileText, Users, FileEdit, X, Loader2, ArrowRight,
  UserCog, LayoutDashboard, Wallet, CalendarDays, Settings,
  Package, Receipt, Calculator, BarChart3, BookOpen, Plus
} from 'lucide-react';

const GlobalSearch = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ invoices: [], quotes: [], clients: [], employees: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const labels = {
    fr: {
      search: 'Rechercher...', minChars: 'Tapez pour rechercher (factures, clients, employés...)',
      noResults: 'Aucun résultat pour', invoices: 'Factures', quotes: 'Devis',
      clients: 'Clients', employees: 'Employés', quickActions: 'Actions rapides',
      navigation: 'Navigation', select: 'Sélectionner', close: 'Fermer',
      navigate: 'Naviguer', unknownClient: 'Client inconnu', noContact: 'Pas de contact'
    },
    de: {
      search: 'Suchen...', minChars: 'Tippen zum Suchen (Rechnungen, Kunden, Mitarbeiter...)',
      noResults: 'Keine Ergebnisse für', invoices: 'Rechnungen', quotes: 'Offerten',
      clients: 'Kunden', employees: 'Mitarbeiter', quickActions: 'Schnellaktionen',
      navigation: 'Navigation', select: 'Auswählen', close: 'Schliessen',
      navigate: 'Navigieren', unknownClient: 'Unbekannter Kunde', noContact: 'Kein Kontakt'
    },
    en: {
      search: 'Search...', minChars: 'Type to search (invoices, clients, employees...)',
      noResults: 'No results for', invoices: 'Invoices', quotes: 'Quotes',
      clients: 'Clients', employees: 'Employees', quickActions: 'Quick actions',
      navigation: 'Navigation', select: 'Select', close: 'Close',
      navigate: 'Navigate', unknownClient: 'Unknown client', noContact: 'No contact'
    }
  };
  const l = labels[language] || labels.fr;

  const quickActions = [
    { id: 'new-invoice', label: { fr: 'Nouvelle facture', de: 'Neue Rechnung', en: 'New invoice' }, icon: Plus, path: '/app/invoices/new', color: 'bg-blue-50 text-blue-600' },
    { id: 'new-quote', label: { fr: 'Nouveau devis', de: 'Neue Offerte', en: 'New quote' }, icon: Plus, path: '/app/quotes/new', color: 'bg-emerald-50 text-emerald-600' },
    { id: 'new-client', label: { fr: 'Nouveau client', de: 'Neuer Kunde', en: 'New client' }, icon: Plus, path: '/app/clients', color: 'bg-purple-50 text-purple-600' },
  ];

  const navItems = [
    { id: 'nav-dashboard', label: { fr: 'Dashboard', de: 'Dashboard', en: 'Dashboard' }, icon: LayoutDashboard, path: '/app/dashboard', color: 'bg-slate-100 text-slate-600' },
    { id: 'nav-invoices', label: { fr: 'Factures', de: 'Rechnungen', en: 'Invoices' }, icon: FileText, path: '/app/invoices', color: 'bg-blue-50 text-blue-600' },
    { id: 'nav-quotes', label: { fr: 'Devis', de: 'Offerten', en: 'Quotes' }, icon: FileEdit, path: '/app/quotes', color: 'bg-emerald-50 text-emerald-600' },
    { id: 'nav-clients', label: { fr: 'Clients', de: 'Kunden', en: 'Clients' }, icon: Users, path: '/app/clients', color: 'bg-purple-50 text-purple-600' },
    { id: 'nav-employees', label: { fr: 'Employes', de: 'Mitarbeiter', en: 'Employees' }, icon: UserCog, path: '/app/employees', color: 'bg-orange-50 text-orange-600' },
    { id: 'nav-payslips', label: { fr: 'Fiches de paie', de: 'Lohnabrechnungen', en: 'Payslips' }, icon: Wallet, path: '/app/payslips', color: 'bg-teal-50 text-teal-600' },
    { id: 'nav-absences', label: { fr: 'Planning', de: 'Planung', en: 'Planning' }, icon: CalendarDays, path: '/app/absences', color: 'bg-amber-50 text-amber-600' },
    { id: 'nav-hr-dashboard', label: { fr: 'Dashboard RH', de: 'HR Dashboard', en: 'HR Dashboard' }, icon: BarChart3, path: '/app/hr-dashboard', color: 'bg-indigo-50 text-indigo-600' },
    { id: 'nav-accounting', label: { fr: 'Comptabilite', de: 'Buchhaltung', en: 'Accounting' }, icon: BookOpen, path: '/app/accounting', color: 'bg-cyan-50 text-cyan-600' },
    { id: 'nav-products', label: { fr: 'Produits', de: 'Produkte', en: 'Products' }, icon: Package, path: '/app/products', color: 'bg-pink-50 text-pink-600' },
    { id: 'nav-expenses', label: { fr: 'Depenses', de: 'Ausgaben', en: 'Expenses' }, icon: Receipt, path: '/app/expenses', color: 'bg-red-50 text-red-600' },
    { id: 'nav-fiscal', label: { fr: 'Bilan fiscal', de: 'Steuerbilanz', en: 'Tax report' }, icon: Calculator, path: '/app/fiscal', color: 'bg-yellow-50 text-yellow-700' },
    { id: 'nav-settings', label: { fr: 'Parametres', de: 'Einstellungen', en: 'Settings' }, icon: Settings, path: '/app/settings', color: 'bg-slate-100 text-slate-600' },
  ];

  // Build flat list of all selectable items for keyboard nav
  const buildFlatItems = useCallback(() => {
    const items = [];
    if (!query || query.length < 2) {
      quickActions.forEach(a => items.push({ type: 'action', data: a }));
      const q_lower = query.toLowerCase();
      const filteredNav = q_lower
        ? navItems.filter(n => n.label[language]?.toLowerCase().includes(q_lower) || n.label.fr?.toLowerCase().includes(q_lower))
        : navItems;
      filteredNav.forEach(n => items.push({ type: 'nav', data: n }));
    } else {
      results.invoices.forEach(inv => items.push({ type: 'invoice', data: inv }));
      results.quotes.forEach(q => items.push({ type: 'quote', data: q }));
      results.clients.forEach(c => items.push({ type: 'client', data: c }));
      results.employees.forEach(e => items.push({ type: 'employee', data: e }));
    }
    return items;
  }, [query, results, language]);

  const flatItems = buildFlatItems();

  // Keyboard shortcut (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Reset selection when results change
  useEffect(() => { setSelectedIdx(0); }, [query, results]);

  // Search debounce
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults({ invoices: [], quotes: [], clients: [], employees: [] });
      return;
    }
    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(query)}`);
        setResults(res.data);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleSelect = (item) => {
    setIsOpen(false);
    setQuery('');
    switch (item.type) {
      case 'invoice': navigate(`/app/invoices/${item.data.id}`); break;
      case 'quote': navigate(`/app/quotes/${item.data.id}`); break;
      case 'client': navigate(`/app/clients/${item.data.id}`); break;
      case 'employee': navigate(`/app/employees`); break;
      case 'action': case 'nav': navigate(item.data.path); break;
      default: break;
    }
  };

  const handleKeyNav = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(prev => Math.min(prev + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatItems.length > 0) {
      e.preventDefault();
      handleSelect(flatItems[selectedIdx]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  const totalResults = results.invoices.length + results.quotes.length + results.clients.length + results.employees.length;

  const ResultItem = ({ item, index }) => {
    const isSelected = index === selectedIdx;
    const baseClass = `w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`;
    
    if (item.type === 'invoice') {
      return (
        <button onClick={() => handleSelect(item)} className={baseClass} data-testid={`search-result-invoice-${item.data.id}`}>
          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-slate-900 truncate">{item.data.invoice_number}</p>
            <p className="text-xs text-slate-500 truncate">{item.data.client_name} {item.data.total ? `- ${item.data.total.toFixed(2)} CHF` : ''}</p>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
        </button>
      );
    }
    if (item.type === 'quote') {
      return (
        <button onClick={() => handleSelect(item)} className={baseClass} data-testid={`search-result-quote-${item.data.id}`}>
          <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <FileEdit className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-slate-900 truncate">{item.data.quote_number}</p>
            <p className="text-xs text-slate-500 truncate">{item.data.client_name} {item.data.total ? `- ${item.data.total.toFixed(2)} CHF` : ''}</p>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
        </button>
      );
    }
    if (item.type === 'client') {
      return (
        <button onClick={() => handleSelect(item)} className={baseClass} data-testid={`search-result-client-${item.data.id}`}>
          <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4 text-purple-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-slate-900 truncate">{item.data.name}</p>
            <p className="text-xs text-slate-500 truncate">{item.data.email || item.data.phone || l.noContact}</p>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
        </button>
      );
    }
    if (item.type === 'employee') {
      return (
        <button onClick={() => handleSelect(item)} className={baseClass} data-testid={`search-result-employee-${item.data.id}`}>
          <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
            <UserCog className="w-4 h-4 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-slate-900 truncate">{item.data.first_name} {item.data.last_name}</p>
            <p className="text-xs text-slate-500 truncate">{item.data.position || item.data.department || item.data.email || ''}</p>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
        </button>
      );
    }
    if (item.type === 'action') {
      const Icon = item.data.icon;
      return (
        <button onClick={() => handleSelect(item)} className={baseClass} data-testid={`search-action-${item.data.id}`}>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${item.data.color}`}>
            <Icon className="w-4 h-4" />
          </div>
          <span className="font-medium text-sm text-slate-900">{item.data.label[language] || item.data.label.fr}</span>
          <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0 ml-auto" />
        </button>
      );
    }
    if (item.type === 'nav') {
      const Icon = item.data.icon;
      return (
        <button onClick={() => handleSelect(item)} className={baseClass} data-testid={`search-nav-${item.data.id}`}>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${item.data.color}`}>
            <Icon className="w-4 h-4" />
          </div>
          <span className="text-sm text-slate-700">{item.data.label[language] || item.data.label.fr}</span>
          <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0 ml-auto" />
        </button>
      );
    }
    return null;
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors w-full max-w-xs"
        data-testid="global-search-trigger"
      >
        <Search className="w-4 h-4" />
        <span className="hidden sm:inline">{l.search}</span>
        <kbd className="hidden sm:inline-flex ml-auto items-center gap-1 px-1.5 py-0.5 text-xs font-mono bg-white rounded border border-slate-200">
          ⌘K
        </kbd>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-xl bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden" data-testid="global-search-modal">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
              <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                placeholder={l.search}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyNav}
                className="flex-1 bg-transparent text-base outline-none placeholder:text-slate-400"
                data-testid="global-search-input"
              />
              {loading && <Loader2 className="w-5 h-5 animate-spin text-blue-500 flex-shrink-0" />}
              <button onClick={() => { setIsOpen(false); setQuery(''); }} className="p-1 hover:bg-slate-100 rounded flex-shrink-0">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="max-h-[400px] overflow-y-auto">
              {(!query || query.length < 2) ? (
                <div className="py-2">
                  <div className="px-4 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">{l.quickActions}</div>
                  {flatItems.filter(i => i.type === 'action').map((item, idx) => (
                    <ResultItem key={item.data.id} item={item} index={idx} />
                  ))}
                  <div className="px-4 py-1.5 mt-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">{l.navigation}</div>
                  {flatItems.filter(i => i.type === 'nav').map((item, idx) => (
                    <ResultItem key={item.data.id} item={item} index={quickActions.length + idx} />
                  ))}
                </div>
              ) : totalResults === 0 && !loading ? (
                <div className="p-6 text-center text-slate-500">
                  <p>{l.noResults} "{query}"</p>
                </div>
              ) : (
                <div className="py-2">
                  {results.invoices.length > 0 && (
                    <>
                      <div className="px-4 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">{l.invoices} ({results.invoices.length})</div>
                      {results.invoices.map((inv, idx) => (
                        <ResultItem key={inv.id} item={{ type: 'invoice', data: inv }} index={idx} />
                      ))}
                    </>
                  )}
                  {results.quotes.length > 0 && (
                    <>
                      <div className="px-4 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">{l.quotes} ({results.quotes.length})</div>
                      {results.quotes.map((q, idx) => (
                        <ResultItem key={q.id} item={{ type: 'quote', data: q }} index={results.invoices.length + idx} />
                      ))}
                    </>
                  )}
                  {results.clients.length > 0 && (
                    <>
                      <div className="px-4 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">{l.clients} ({results.clients.length})</div>
                      {results.clients.map((c, idx) => (
                        <ResultItem key={c.id} item={{ type: 'client', data: c }} index={results.invoices.length + results.quotes.length + idx} />
                      ))}
                    </>
                  )}
                  {results.employees.length > 0 && (
                    <>
                      <div className="px-4 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">{l.employees} ({results.employees.length})</div>
                      {results.employees.map((emp, idx) => (
                        <ResultItem key={emp.id} item={{ type: 'employee', data: emp }} index={results.invoices.length + results.quotes.length + results.clients.length + idx} />
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-xs text-slate-500 flex items-center gap-4">
              <span><kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">↑↓</kbd> {l.navigate}</span>
              <span><kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">↵</kbd> {l.select}</span>
              <span className="ml-auto"><kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">Esc</kbd> {l.close}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
